import { useEffect, useState } from 'react'
import { RecurlyError } from '@recurly/recurly-js'
import { checkoutHooks } from '@pff-consumer/data-provider/lib/checkout/checkout.hooks'
import { SubscriptionPlans } from '@pff-consumer/schema'
import { defaultAnnualPrice, defaultMonthlyPrice } from '@pff-consumer/utils'
import { usePricing } from './usePricing'

const { usePromoCode } = checkoutHooks

export const useSubscriptionPricing = (monthlyPlan: SubscriptionPlans) => {
  const coupon = usePromoCode()
  const [recurlyMonthlyError, setRecurlyMonthlyError] = useState<RecurlyError>()
  const [recurlyYearlyError, setRecurlyYearlyError] = useState<RecurlyError>()

  const {
    price: annualPlanPrice,
    updatePrice: updateAnnualPrice,
    loading: annualPriceLoading,
  } = usePricing(coupon, SubscriptionPlans.PREMIUM_ANNUAL, setRecurlyYearlyError)

  const {
    price: monthlyPlanPrice,
    updatePrice: updateMonthlyPrice,
    loading: monthlyPriceLoading,
  } = usePricing(coupon, monthlyPlan, setRecurlyMonthlyError)

  useEffect(() => {
    updateAnnualPrice(SubscriptionPlans.PREMIUM_ANNUAL, coupon)
    updateMonthlyPrice(monthlyPlan, coupon)
    setRecurlyMonthlyError(undefined)
    setRecurlyYearlyError(undefined)
    // updateAnnualPrice and updateMonthlyPrice come from Recurly and aren't stable
    // eslint-disable-next-line
  }, [coupon, monthlyPlan])

  const isError = recurlyMonthlyError && recurlyYearlyError
  /**
   * Annual pricing returns 0$ in annualPlanPrice.now and the actual price in annualPlanPrice.next as there is a 7 day trial period
   * Monthly pricing returns actual price in monthlyPlanPrice.now and then the price of following month in monthlyPlanPrice.next as there is no trial period
   * For discounts calculation use annualPlanPrice.next for annual and monthlyPlanPrice.now for monthly
   */
  const isDiscountApplied = annualPlanPrice?.now?.discount !== '0.00' || monthlyPlanPrice?.now?.discount !== '0.00'

  return {
    annualPlanPrice: annualPlanPrice || defaultAnnualPrice,
    annualPriceLoading,
    monthlyPlanPrice: monthlyPlanPrice || defaultMonthlyPrice,
    monthlyPriceLoading,
    isError,
    isDiscountApplied,
  }
}
